import React, { useState, useEffect } from "react";
import { Table, Input } from "antd";
import { CheckOutlined } from '@ant-design/icons';
import { ref, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage'
import EventsModal from './EventsModal';
import isEqual from 'lodash.isequal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { db, storage } from '../../../db/db';

dayjs.extend(timezone);
dayjs.extend(utc);

function EventsTable({ committeeData }) {
  const [data, setData] = useState([]);
  const [info, setInfo] = useState({});
  const [open, setOpen] = useState(false);
  const [searchedText, setSearchedText] = useState("");
  const [itemsToDelete, setItemsToDelete] = useState([])
  const [fileList, setFileList] = useState()
  const [submitting, setSubmitting] = useState();
  const [quillValue, setQuillValue] = useState('');
  const [noDate, setNoDate] = useState(false);
  const [uponAdjourn, setUponAdjourn] = useState(false);
  const [useAgenda, setUseAgenda] = useState(false);
  const [unchangedData, setUnchangedData] = useState();

  const eventCol = [
    {
      title: 'Date/Time',
      dataIndex: 'start',
      key: 'date',
      width: 80,
      render: date => date ? dayjs(date).utc().tz('America/New_York').format("YYYY-MM-DD hh:mm a") : null
    },
    {
      title: 'Committees',
      key: 'committees',
      dataIndex: 'committees',
      width: 250,
      ellipsis: true,
      filteredValue: [searchedText],
      onFilter: (value, record) => {
        return String(record.subject)
          .toLowerCase()
          .includes(value.toLowerCase()) ||
          String(record.billsResolutions)
            .toLowerCase()
            .includes(value.toLowerCase()) ||
          String(record.committees)
            .toLowerCase()
            .includes(value.toLowerCase()) ||
          String(record.start)
            .toLowerCase()
            .includes(value.toLowerCase())
      }
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: 200,
    },
    {
      title: 'Location',
      key: 'location',
      dataIndex: 'location',
      width: 100,
    },
    {
      title: 'Bills/Resolutions',
      key: 'billsResolutions',
      dataIndex: 'billsResolutions',
      width: 100,
    },
    {
      title: 'Agenda Link',
      key: 'useAgendaLink',
      dataIndex: 'useAgenda',
      width: 63,
      ellipsis: true,
      render: (useAgenda, record) =>
        !useAgenda && record.agendaUri ? <CheckOutlined /> : null,
    },
    {
      title: 'Agenda',
      key: 'useAgenda',
      dataIndex: 'useAgenda',
      width: 50,
      ellipsis: true,
      render: (useAgenda, record) =>
        useAgenda && record.agenda ? <CheckOutlined /> : null,
    },
    {
      title: 'Docs #',
      key: 'docCount',
      dataIndex: 'docCount',
      width: 50,
      ellipsis: true,
    },
  ]

  useEffect(() => {
    const unsubscribe = db.collection('trackedEvents').onSnapshot(snapshot => {
      const eventArr = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        let isAgenda = false;
        let isAgendaLink = false;
        const bills = data.billsResolutions?.join(", ");
        const docsArr = [];
        let quill = null;

        if (data.agenda) {
          isAgenda = data.agenda === "\"<p><br></p>\"" ? false : true;
          quill = data.agenda?.startsWith('"') ? JSON.parse(data.agenda) : data.agenda;
        }

        if (data.agendaUri) {
          isAgendaLink = true;
        }

        if (data.extraDocuments?.length > 0) {
          data.extraDocuments.forEach(doc => docsArr.push(doc));
        }

        eventArr.push({
          pageType: 'Event',
          key: data.id,
          subject: data.subject,
          id: data.id,
          start: data.start,
          end: data.end,
          body: data.body,
          chamber: data.chamber,
          isVimeo: data.isVimeo,
          willBroadcast: data.willBroadcast,
          location: data.location,
          livestreamUrl: data.livestreamUrl,
          committees: data.committees,
          billsResolutions: bills,
          agendaBool: isAgenda,
          agenda: quill,
          docCount: data.extraDocuments?.length,
          extraDocuments: docsArr,
          noDate: data.noDate,
          noWebCal: data.noWebCal,
          uponAdjourn: data.uponAdjourn,
          adjournText: data.adjournText,
          agendaLinkBool: isAgendaLink,
          agendaUri: data.agendaUri,
          useAgenda: data.useAgenda,
          agendaNotes: data.agendaNotes || '',
          isUpdated: data.isUpdated,
          updates: data.updates,
          autoUpdate: true,
          notifyUsers: true,
        });
      });

      // **Updated Sorting Logic**
      eventArr.sort((a, b) => {
        if (a.isUpdated && !b.isUpdated) return -1; // Move updated events to the top
        if (!a.isUpdated && b.isUpdated) return 1;  // Move non-updated events below updated ones
        const dateA = new Date(a.start);
        const dateB = new Date(b.start);
        if (!b.start) return 1;
        if (!a.start) return -1;
        return dateB - dateA; // Sort remaining events by start date (newest first)
      });

      setData(eventArr);
    });

    return () => unsubscribe(); // Cleanup listener when component unmounts
  }, [committeeData]);


  const rowClicked = (e, record) => {
    if (record.isUpdated) {
      db.collection('trackedEvents').doc(record.id).update({
        isUpdated: false,
        dontNotify: true,
      })
    }
    const data = {
      ...record,
      start: record.start ? record.start : dayjs().utc().tz('America/New_York'),
      end: record.end ? record.end : dayjs().utc().tz('America/New_York').add(30, 'minute'),
    }
    setUnchangedData(data)
    setQuillValue(record.agenda || "");
    setNoDate(record.noDate ? record.noDate : false)
    setUponAdjourn(record.uponAdjourn ? record.uponAdjourn : false)
    setInfo(data)
    setItemsToDelete([])
  };

  const handleChange = ({ fileList }) =>
    setFileList(fileList.filter(file => file.status !== "error"));

  const onRemove = async file => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);

    setFileList(newFileList);
  };

  const storageUpload = async (saveData) => {
    setSubmitting(true);
    if (fileList?.length > 0) {
      const fileArr = [];
      await Promise.all(
        fileList?.map(async file => {
          const fileName = `${file.name}-${Date.now()}`
          const pdfRef = ref(storage, `${saveData.id}/${fileName}`);
          try {
            await uploadBytes(pdfRef, file.originFileObj)
            const downloadUrl = await getDownloadURL(pdfRef);
            const item = {
              name: fileName,
              url: downloadUrl,
            };
            fileArr.push(item)
          } catch (e) {
            console.log(e);
          }
        })
      )
      return fileArr
    } else {
      return []
    }
  }

  const onSubmit = async (saveData) => {
    if (saveData.agenda === "<p><br></p>") {
      saveData.agenda = null;
    }
    const updateFields = {
      start: 'Date/Time',
      adjournText: 'Date/Time',
      agendaUri: 'Agenda',
      agendaNotes: 'Agenda Notes',
      agenda: 'Agenda',
      livestreamUrl: 'Livestream Link',
      location: 'Location',
      subject: 'Subject',
      billsResolutions: 'Bills',
      committees: 'Committees',
      extraDocuments: 'Extra Documents'
    };
    let updates = [];
    let updateObj

    if (unchangedData) {
      // Compare only the fields that exist in both saveData and unchangedData
      // and make sure changing null to "" wont register an update
      updates = [...new Set(Object.keys(saveData)
        .filter(key => updateFields[key] && !isEqual(
          saveData[key] ?? "",
          unchangedData[key] ?? ""
        ))
        .map(key => updateFields[key])
      )];
  
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      };
      const formattedDate = new Intl.DateTimeFormat('en-US', options).format(new Date());
      updateObj = {
        date: formattedDate,
        updates,
      };
    }

    try {
      const storedFiles = await storageUpload(saveData)
      const startDate = dayjs(saveData.start).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      const endDate = dayjs(saveData.end).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      const bills = (saveData.billsResolutions || '')  // Ensure it's a string
        .replace(/\s/g, '')  // Remove spaces
        .split(',')          // Split into array
        .filter(bill => bill); // Remove empty strings
      const formattedBills = bills.length ? bills : null;
      let quillStore = null;
      if (quillValue && quillValue !== "<p><br></p>") {
        quillStore = quillValue || null;
      }
      db.collection('trackedEvents').doc(saveData.id).set({
        autoUpdate: saveData.autoUpdate,
        dontNotify: !saveData.notifyUsers,
        id: saveData.id ? saveData.id : null,
        agendaUri: saveData.agendaUri ? saveData.agendaUri : null,
        livestreamUrl: saveData.livestreamUrl ? saveData.livestreamUrl : null,
        location: saveData.location ? saveData.location : null,
        start: startDate,
        end: noDate ? null : endDate,
        subject: saveData.subject ? saveData.subject : null,
        committees: saveData.committees ? saveData.committees : saveData.committees !== null ? saveData.committees : null,
        billsResolutions: formattedBills,
        body: info.body ? info.body : null,
        chamber: saveData.chamber ? saveData.chamber : null,
        isVimeo: info.isVimeo ? info.isVimeo : null,
        willBroadcast: info.willBroadcast ? info.willBroadcast : null,
        extraDocuments: info.extraDocuments && info.extraDocuments.length ? [...storedFiles, ...info.extraDocuments] : storedFiles.length ? [...storedFiles] : null,
        agenda: quillStore ? quillStore : null,
        noDate: noDate,
        noWebCal: info.noWebCal ? info.noWebCal : false,
        uponAdjourn: noDate ? false : uponAdjourn,
        adjournText: saveData.adjournText ? saveData.adjournText : null,
        useAgenda: useAgenda,
        agendaNotes: saveData.agendaNotes ? saveData.agendaNotes : '',
        updates: updates.length > 0 ? [updateObj, ...(unchangedData?.updates || [])] : unchangedData?.updates || [],
      }, { merge: true })
        .then(() => {
          // getEventData();
          setOpen(false);
        })
      setFileList([]);
      setQuillValue('')
      setInfo({})
      setUnchangedData(null)
    } catch (err) {
      console.log(err);
    } finally {
      setSubmitting(false);
      onSubmitDeleteFiles()
    }
  };

  const handleDeleteFiles = (fileName) => {
    const newArr = [...itemsToDelete, fileName]
    setItemsToDelete(newArr)
    setInfo({
      ...info,
      extraDocuments: info.extraDocuments.filter(item => item.name !== fileName)
    })
  };

  const onSubmitDeleteFiles = () => {
    for (let i = 0; i < itemsToDelete.length; i += 1) {
      const pdfRef = ref(storage, `${info.id}/${itemsToDelete[i]}`)
      deleteObject(pdfRef).then(() => {
        // File deleted successfully
      })
    }
    setItemsToDelete([])
  };

  const deleteEvent = (event, files) => {
    // Delete associated files from storage
    for (let i = 0; i < files.length; i += 1) {
      const pdfRef = ref(storage, `${event.id}/${files[i].name}`);
      deleteObject(pdfRef).catch(() => {
        alert("Error removing extra documents!");
      });
    }

    // Work on removing the notification of the event from users who have the event in allTracked so they cant click on an unavailable notification!!!

    // Delete the event from Firestore
    db.collection("trackedEvents").doc(event.id).delete()
      .then(() => {
        alert("Event successfully deleted!");
        // getEventData();
        setOpen(false);
        setFileList([]);
        setQuillValue('');
        setInfo({})
      })
      .catch(() => {
        alert("Error removing event!");
      });
  };

  const centerPagination = "bottomCenter";
  return (
    <div>
      <Input.Search
        placeholder='Search Tracked Events'
        style={{ marginBottom: 8 }}
        onSearch={(value) => {
          setSearchedText(value);
        }}
        onChange={(e) => {
          setSearchedText(e.target.value)
        }}
      />
      <Table
        onRow={(record) => ({
          onClick: (event) => {
            rowClicked(event, record);
          },
        })}
        bordered
        rowHoverable={false}
        size={'small'}
        columns={eventCol}
        dataSource={data}
        rowClassName={(record) => (record.isUpdated ? "isUpdated" : "")}
        pagination={{
          position: [centerPagination],
          hideOnSinglePage: true,
        }}
        scroll={{ y: 500 }}
        footer={() =>
          <EventsModal
            info={info}
            setInfo={setInfo}
            tableType={'Tracked Event'}
            onSubmit={onSubmit}
            open={open}
            setOpen={setOpen}
            committeeData={committeeData}
            handleDeleteFiles={handleDeleteFiles}
            handleChange={handleChange}
            onRemove={onRemove}
            submitting={submitting}
            setFileList={setFileList}
            fileList={fileList}
            quillValue={quillValue}
            setQuillValue={setQuillValue}
            noDate={noDate}
            setNoDate={setNoDate}
            uponAdjourn={uponAdjourn}
            setUponAdjourn={setUponAdjourn}
            useAgenda={useAgenda}
            setUseAgenda={setUseAgenda}
            deleteEvent={deleteEvent}
          setUnchangedData={setUnchangedData}
          />
        }
      />
    </div>
  );
}

export default EventsTable;